/**
 * Media store — photos, video and voice notes as Blobs in IndexedDB.
 *
 * NOT base64. The prototype stored every photo as a dataURL inside the supplier
 * record, which cannot survive production: base64 adds a third to an already
 * large payload, and MongoDB's document ceiling is 16MB while the spec allows
 * 25MB videos. Here the record holds only a reference; the bytes live in this
 * store until they are uploaded, and on disk under BEENEXA_HOME after that.
 *
 * Blobs are kept even after a successful upload so the show floor stays fully
 * offline-capable — eviction only happens under quota pressure, and only for
 * rows that have an `uploadedPath`.
 */

import { all, allByIndex, del, get, put } from './db';
import { STORE } from './schema';
import { uid, nowMs } from './entities';
import {
  MAIN_PHOTO_TAG,
  VIDEO_MAX_MB,
  VIDEO_MAX_SEC,
  VIDEO_TOLERANCE_SEC,
} from './constants';

export const KIND = { PHOTO: 'photo', VIDEO: 'video', VOICE: 'voice' };

/**
 * Store a captured Blob.
 *
 * `pendingUpload` is 1 while the file still needs to reach the server and is
 * deleted (not set to 0) once uploaded — IndexedDB omits records whose indexed
 * key is undefined, so the index holds exactly the upload queue.
 */
export async function putMedia({
  blob,
  kind,
  tag = '',
  supplierId = '',
  productId = '',
  contactId = '',
  duration = null,
  width = null,
  height = null,
}) {
  const row = {
    id: uid(),
    kind,
    tag,
    supplierId,
    productId,
    contactId,
    blob,
    size: blob.size,
    type: blob.type,
    duration,
    width,
    height,
    createdAt: nowMs(),
    pendingUpload: 1,
    uploadedPath: null,
  };
  await put(STORE.MEDIA, row);
  return reference(row);
}

/**
 * What goes INSIDE the supplier record — never the Blob. `mediaId` resolves
 * locally; `path` resolves from the server once uploaded.
 */
export function reference(row) {
  return {
    id: row.id,
    mediaId: row.id,
    kind: row.kind,
    tag: row.tag,
    productId: row.productId || '',
    contactId: row.contactId || '',
    duration: row.duration,
    size: row.size,
    type: row.type,
    path: row.uploadedPath || null,
    createdAt: row.createdAt,
  };
}

export const getMedia = (id) => get(STORE.MEDIA, id);

export const pendingUploads = () => allByIndex(STORE.MEDIA, 'pendingUpload', 1);

export const mediaForSupplier = (supplierId) =>
  allByIndex(STORE.MEDIA, 'supplierId', supplierId);

export async function markUploaded(id, uploadedPath) {
  const row = await get(STORE.MEDIA, id);
  if (!row) return null;
  const next = { ...row, uploadedPath };
  delete next.pendingUpload;
  await put(STORE.MEDIA, next);
  return next;
}

export async function deleteMedia(id) {
  releaseUrl(id);
  await del(STORE.MEDIA, id);
}

/* ── Object URL cache ───────────────────────────────────────────────────── */
/**
 * createObjectURL leaks until revoked, and a photo grid mounts and unmounts
 * constantly while scrolling. Cache one URL per media id and let callers
 * release it.
 */
const urls = new Map();

export async function mediaUrl(id) {
  if (urls.has(id)) return urls.get(id);
  const row = await get(STORE.MEDIA, id);
  if (!row || !row.blob) return null;
  const url = URL.createObjectURL(row.blob);
  urls.set(id, url);
  return url;
}

export function releaseUrl(id) {
  const url = urls.get(id);
  if (url) {
    URL.revokeObjectURL(url);
    urls.delete(id);
  }
}

export function releaseAllUrls() {
  urls.forEach((url) => URL.revokeObjectURL(url));
  urls.clear();
}

/* ── Validation (rule V1) ───────────────────────────────────────────────── */

/**
 * Rule V1: accept ≤20s (+2s tolerance) and ≤25MB; otherwise toast and DO NOT
 * store. Returns { ok } or { ok: false, reason } with user-facing wording.
 */
export function checkVideo({ duration, size }) {
  const maxBytes = VIDEO_MAX_MB * 1024 * 1024;
  if (size > maxBytes) {
    return {
      ok: false,
      reason: `That video is ${(size / 1024 / 1024).toFixed(0)} MB. Keep it under ${VIDEO_MAX_MB} MB.`,
    };
  }
  if (duration != null && duration > VIDEO_MAX_SEC + VIDEO_TOLERANCE_SEC) {
    return {
      ok: false,
      reason: `That video is ${Math.round(duration)}s. Keep it under ${VIDEO_MAX_SEC}s.`,
    };
  }
  return { ok: true };
}

/* ── Main-photo bookkeeping (rule Q1) ───────────────────────────────────── */

/**
 * "Main Product Photo" is unique per product: re-tagging one demotes the
 * previous main to 'Product'. Returns a new photos array.
 */
export function applyMainPhotoTag(photos, photoId, productId) {
  return photos.map((p) => {
    if (p.id === photoId) return { ...p, tag: MAIN_PHOTO_TAG };
    const sameProduct = (p.productId || '') === (productId || '');
    if (sameProduct && p.tag === MAIN_PHOTO_TAG) return { ...p, tag: 'Product' };
    return p;
  });
}

/** Total bytes held locally — drives the storage warning. */
export async function localBytes() {
  const rows = await all(STORE.MEDIA);
  return rows.reduce((n, r) => n + (r.size || 0), 0);
}

/**
 * Free space by dropping Blobs that are already on the server, oldest first.
 * The row stays (so the reference still resolves via `uploadedPath`); only the
 * bytes go. Never touches anything still pending upload.
 */
export async function evictUploaded(targetBytes) {
  const rows = (await all(STORE.MEDIA))
    .filter((r) => r.uploadedPath && r.blob)
    .sort((a, b) => a.createdAt - b.createdAt);

  let freed = 0;
  for (const row of rows) {
    if (freed >= targetBytes) break;
    releaseUrl(row.id);
    const next = { ...row, blob: null, evicted: true };
    await put(STORE.MEDIA, next);
    freed += row.size || 0;
  }
  return freed;
}
